import React from 'react';
import ConfirmModal from './components/ConfirmModal';

// ═══════════════════════════════════════════════════════════════
// Archive — Flat-list repository of all user content + recordings
// ═══════════════════════════════════════════════════════════════

const ARCHIVE_TYPES = [
  { id: 'story',            icon: '', label: 'Stories',           desc: 'Written stories, memories, and AI-assisted writing' },
  { id: 'memory_interview', icon: '', label: 'Interviews',        desc: 'Conversation transcripts from guided interviews' },
  { id: 'recipe',           icon: '', label: 'Recipes',           desc: 'Family recipes passed down through generations' },
  { id: 'poem',             icon: '📜', label: 'Poems & Prayers',   desc: 'Poems, prayers, songs, and sacred words' },
  { id: 'tradition',        icon: '🎄', label: 'Family Traditions', desc: 'Rituals, customs, and things your family always did' },
  { id: 'note',             icon: '', label: 'Notes & Quotes',    desc: 'Quotes, sayings, and short notes worth keeping' },
  { id: 'estate',           icon: '', label: 'Estate Documents',  desc: 'Estate plans, letters of instruction, and legal notes' },
  { id: 'journal',          icon: '📓', label: 'Journal',           desc: 'Personal journal entries — thoughts, reflections, and voice notes' },
  { id: 'recording',        icon: '', label: 'Recordings',        desc: 'Audio and video recordings linked to your stories' },
];

const TYPE_MAP = {};
ARCHIVE_TYPES.forEach(t => { TYPE_MAP[t.id] = t; });

const normalizeType = (type) => {
  if (!type || type === 'story') return 'story';
  if (TYPE_MAP[type]) return type;
  return 'story';
};

// Strip markdown/tags from content for snippet display
const getSnippet = (content, maxLen = 100) => {
  if (!content) return '';
  const clean = content
    .replace(/\[audio:[^\]]*\]/g, '')
    .replace(/\[video:[^\]]*\]/g, '')
    .replace(/#{1,6}\s/g, '')
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')
    .replace(/\n+/g, ' ')
    .trim();
  return clean.length > maxLen ? clean.slice(0, maxLen) + '...' : clean;
};

const SOURCE_LABELS = {
  DEDICATED_MESSAGE: 'Chapter recording',
  INTERVIEW_RECORDING: 'Interview',
  VOICE_RECORDING: 'Voice recording',
  VIDEO_RECORDING: 'Video recording',
};

// ── Dropdown Menu ──────────────────────────────────────────────
function DropdownMenu({ items, onClose, menuRef }) {
  React.useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose, menuRef]);

  return (
    <div style={{
      position: 'fixed', right: 16, zIndex: 1000, marginTop: 4,
      background: '#fff', border: '1px solid rgba(15,23,42,0.12)', borderRadius: 10,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 180, overflow: 'hidden',
    }}>
      {items.map((item, idx) => {
        if (item.separator) return <div key={idx} style={{ height: 1, background: 'rgba(15,23,42,0.08)', margin: '4px 0' }} />;
        return (
          <button key={idx} onClick={() => { item.onClick(); onClose(); }}
            disabled={item.disabled}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              padding: '10px 16px', background: 'none', border: 'none', cursor: item.disabled ? 'default' : 'pointer',
              fontFamily: "'Lora', serif", fontSize: '0.95rem', textAlign: 'left',
              color: item.danger ? '#c06060' : item.disabled ? '#c4b8ac' : '#0f172a',
              transition: 'background 0.1s',
            }}
            onMouseOver={e => { if (!item.disabled) e.currentTarget.style.background = 'rgba(15,23,42,0.03)'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'none'; }}
          >
            {item.icon && <span style={{ fontSize: '1rem', width: 20, textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Archive Card ───────────────────────────────────────────────
function ArchiveCard({ item, bookStoryIds, onView, onEdit, onDelete, onAddToBook, onRemoveFromBook,
  onNavigateToJournal, onPlayRecording, onDeleteRecording, onUnlinkRecording }) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);
  const [pendingConfirm, setPendingConfirm] = React.useState(null); // { type: 'delete'|'unlink'|'deleteRecording' }

  const isRecording = item.type === 'recording';
  const isJournal = item._isJournal;
  const inBook = isRecording ? bookStoryIds.includes(item.storyId) : bookStoryIds.includes(item.id);
  const typeInfo = TYPE_MAP[item.type] || TYPE_MAP.story;
  const dateStr = item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  const snippet = isRecording
    ? (SOURCE_LABELS[item.sourceType] || item.mediaType || 'Recording')
    : getSnippet(item.content);
  const wordCount = (!isRecording && item.content) ? item.content.split(/\s+/).filter(Boolean).length : 0;
  const hasVoice = !isRecording && item.content && item.content.includes('[audio:');

  const handleCardClick = () => {
    if (isRecording && onPlayRecording) onPlayRecording(item.s3Key, item.mediaType);
    else if (isJournal && onNavigateToJournal) onNavigateToJournal();
    else if (onView) onView(item);
  };

  // Build menu items based on type
  const getMenuItems = () => {
    if (isRecording) {
      return [
        { icon: '▶', label: 'Play', onClick: () => onPlayRecording(item.s3Key, item.mediaType) },
        { separator: true },
        { icon: '🔗', label: 'Remove public link', danger: true, onClick: () => {
          setPendingConfirm({ type: 'unlink' });
        }},
        { icon: '🗑', label: 'Delete recording', danger: true, onClick: () => {
          setPendingConfirm({ type: 'deleteRecording' });
        }},
      ];
    }
    if (isJournal) {
      return [
        { icon: '📓', label: 'Open in Journal', onClick: () => onNavigateToJournal() },
      ];
    }
    // Regular story/content items
    const items = [
      { icon: '👁', label: 'View', onClick: () => onView(item) },
      { icon: '✏️', label: 'Edit', onClick: () => onEdit(item) },
    ];
    if (!inBook) {
      items.push({ icon: '', label: 'Add to book', onClick: () => onAddToBook(item.id) });
    } else {
      items.push({ icon: '✓', label: 'Remove from book', onClick: () => onRemoveFromBook(item.id) });
    }
    items.push({ separator: true });
    items.push({ icon: '🗑', label: 'Delete', danger: true, onClick: () => {
      setPendingConfirm({ type: 'delete' });
    }});
    return items;
  };

  return (
    <>
    <div
      onClick={handleCardClick}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 14,
        padding: '16px 20px', background: '#fff',
        border: '1px solid rgba(15,23,42,0.08)', borderRadius: 14,
        marginBottom: 8, cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s',
      }}
      onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(15,23,42,0.18)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}
      onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(15,23,42,0.08)'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Left: content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Title row */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontFamily: "'Lora', serif", fontSize: '1.05rem', fontWeight: 600, color: '#0f172a',
        }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.title || 'Untitled'}
          </span>
          {inBook && (
            <span style={{
              fontSize: '0.8rem', padding: '2px 8px', background: 'rgba(104,211,145,0.1)',
              border: '1px solid rgba(104,211,145,0.25)', borderRadius: 4, color: '#4aa872',
              fontFamily: "'Lora', serif", fontWeight: 500, flexShrink: 0, whiteSpace: 'nowrap',
            }}>In Book</span>
          )}
          {isRecording && (
            <span style={{
              fontSize: '0.8rem', padding: '2px 8px',
              background: item.mediaType === 'VIDEO' ? 'rgba(100,140,220,0.1)' : 'rgba(180,140,80,0.1)',
              border: `1px solid ${item.mediaType === 'VIDEO' ? 'rgba(100,140,220,0.25)' : 'rgba(180,140,80,0.25)'}`,
              borderRadius: 4,
              color: item.mediaType === 'VIDEO' ? '#5a7ab8' : '#8a6a30',
              fontFamily: "'Lora', serif", fontWeight: 500, flexShrink: 0, whiteSpace: 'nowrap',
            }}>{item.mediaType === 'VIDEO' ? 'Video' : 'Audio'}</span>
          )}
          {hasVoice && !isRecording && (
            <span style={{ fontSize: '0.9rem', flexShrink: 0 }}>🎙</span>
          )}
        </div>

        {/* Snippet */}
        {snippet && (
          <div style={{
            fontSize: '0.9rem', color: '#3c4653', marginTop: 4,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            fontFamily: "'Lora', serif",
          }}>
            {snippet}
          </div>
        )}

        {/* Meta row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          {dateStr && (
            <span style={{ fontSize: '0.85rem', color: '#3c4653', fontFamily: "'Lora', serif" }}>{dateStr}</span>
          )}
          {wordCount > 0 && (
            <span style={{ fontSize: '0.85rem', color: '#3c4653', fontFamily: "'Lora', serif" }}>{wordCount} words</span>
          )}
          {/* Type pill */}
          <span style={{
            fontSize: '0.75rem', padding: '1px 8px', background: 'rgba(15,23,42,0.04)',
            borderRadius: 100, color: '#3c4653', fontFamily: "'Lora', serif",
          }}>
            {typeInfo.icon} {typeInfo.label}
          </span>
          {/* Mood for journal */}
          {isJournal && item.mood && (() => {
            const moods = { happy: '😊', calm: '😌', reflective: '🤔', sad: '😢', frustrated: '😤', grateful: '🙏', motivated: '💪', tired: '😴' };
            return moods[item.mood] ? <span style={{ fontSize: '1rem' }}>{moods[item.mood]}</span> : null;
          })()}
        </div>
      </div>

      {/* Right: three-dot menu */}
      <div ref={menuRef} style={{ position: 'relative', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            width: 32, height: 32, borderRadius: '50%', border: 'none',
            background: menuOpen ? 'rgba(15,23,42,0.08)' : 'transparent',
            cursor: 'pointer', fontSize: '1.1rem', color: '#3c4653',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.15s',
          }}
          onMouseOver={e => { if (!menuOpen) e.currentTarget.style.background = 'rgba(15,23,42,0.06)'; }}
          onMouseOut={e => { if (!menuOpen) e.currentTarget.style.background = 'transparent'; }}
        >
          ···
        </button>
        {menuOpen && (
          <DropdownMenu
            items={getMenuItems()}
            onClose={() => setMenuOpen(false)}
            menuRef={menuRef}
          />
        )}
      </div>
    </div>

    {pendingConfirm?.type === 'unlink' && (
      <ConfirmModal
        title="Remove public link?"
        message="The QR code for this recording will stop working. The file stays in your storage."
        confirmLabel="Remove link"
        cancelLabel="Cancel"
        destructive
        onConfirm={() => { setPendingConfirm(null); onUnlinkRecording(item.id); }}
        onCancel={() => setPendingConfirm(null)}
      />
    )}

    {pendingConfirm?.type === 'deleteRecording' && (
      <ConfirmModal
        title="Delete this recording?"
        message="The file and its public QR link will be permanently deleted. This can't be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
        onConfirm={() => { setPendingConfirm(null); onDeleteRecording(item.id, item.s3Key); }}
        onCancel={() => setPendingConfirm(null)}
      />
    )}

    {pendingConfirm?.type === 'delete' && (
      <ConfirmModal
        title={`Delete "${item.title}"?`}
        message="This can't be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
        onConfirm={() => { setPendingConfirm(null); onDelete(item.id); }}
        onCancel={() => setPendingConfirm(null)}
      />
    )}
    </>
  );
}

// ── Add New Item Modal (unchanged) ───────────────────────────────
function AddNewModal({ onClose, onSave, saving }) {
  const [selectedType, setSelectedType] = React.useState(null);
  const [title, setTitle] = React.useState('');
  const [content, setContent] = React.useState('');

  const addableTypes = ARCHIVE_TYPES.filter(t =>
    t.id !== 'estate' && t.id !== 'memory_interview' && t.id !== 'journal' && t.id !== 'recording'
  );

  const handleSave = () => {
    if (!title.trim() || !content.trim()) return;
    onSave({ title: title.trim(), content: content.trim(), type: selectedType || 'story' });
  };

  if (!selectedType) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }} onClick={onClose} />
        <div style={{ position: 'relative', background: '#fbf8f1', borderRadius: 20, padding: '32px 28px', maxWidth: 480, width: '100%', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h3 style={{ fontFamily: "'Lora', serif", fontSize: '1.2rem', color: '#0f172a', margin: 0 }}>What are you adding?</h3>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#3c4653', padding: 4 }}>×</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {addableTypes.map(type => (
              <button key={type.id} onClick={() => setSelectedType(type.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
                  background: '#fff', border: '1.5px solid rgba(15,23,42,0.1)', borderRadius: 12,
                  cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s', width: '100%',
                }}
                onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(15,23,42,0.3)'; e.currentTarget.style.background = 'rgba(15,23,42,0.02)'; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(15,23,42,0.1)'; e.currentTarget.style.background = '#fff'; }}
              >
                <span style={{ fontSize: '1.4rem', flexShrink: 0 }}>{type.icon}</span>
                <div>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: '1rem', fontWeight: 600, color: '#0f172a' }}>{type.label}</div>
                  <div style={{ fontSize: '1rem', color: '#3c4653', marginTop: 2 }}>{type.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const typeInfo = TYPE_MAP[selectedType];
  const placeholders = {
    story: { title: 'e.g., The Lesson My Father Taught Me', content: 'Write your story, memory, or advice here...' },
    recipe: { title: "e.g., Grandma's Apple Pie", content: 'Ingredients, instructions, and any notes about this recipe...' },
    poem: { title: 'e.g., A Prayer for My Grandchildren', content: 'Write or paste the poem, prayer, or lyrics here...' },
    tradition: { title: 'e.g., Christmas Morning Pancakes', content: 'Describe this tradition — what you do, when, and why it matters...' },
    note: { title: 'e.g., Something Dad Always Said', content: 'The quote, saying, or note you want to preserve...' },
  };
  const ph = placeholders[selectedType] || placeholders.story;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }} onClick={onClose} />
      <div style={{ position: 'relative', background: '#fbf8f1', borderRadius: 20, padding: '32px 28px', maxWidth: 540, width: '100%', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <button onClick={() => setSelectedType(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Lora', serif", fontSize: '1rem', color: '#3c4653', padding: '4px 0' }}>← Back</button>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#3c4653', padding: 4 }}>×</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <span style={{ fontSize: '1.3rem' }}>{typeInfo.icon}</span>
          <h3 style={{ fontFamily: "'Lora', serif", fontSize: '1.15rem', color: '#0f172a', margin: 0 }}>New {typeInfo.label.replace(/s$/, '')}</h3>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', marginBottom: 8 }}>Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder={ph.title}
            style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1.5px solid rgba(15,23,42,0.15)', fontFamily: "'Lora', serif", fontSize: '1rem', color: '#0f172a', background: '#fff', outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', marginBottom: 8 }}>Content</label>
          <textarea value={content} onChange={e => setContent(e.target.value)} placeholder={ph.content} rows={8}
            style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1.5px solid rgba(15,23,42,0.15)', fontFamily: "'Lora', serif", fontSize: '1rem', color: '#0f172a', background: '#fff', outline: 'none', resize: 'vertical', lineHeight: 1.7, boxSizing: 'border-box' }}
          />
        </div>
        <button onClick={handleSave} disabled={saving || !title.trim() || !content.trim()}
          style={{
            width: '100%', padding: '14px', borderRadius: 12, border: 'none',
            background: (!title.trim() || !content.trim()) ? 'rgba(15,23,42,0.1)' : '#0f172a',
            color: (!title.trim() || !content.trim()) ? '#3c4653' : '#fbf8f1',
            fontFamily: "'Lora', serif", fontSize: '1rem', fontWeight: 600,
            cursor: (!title.trim() || !content.trim()) ? 'default' : 'pointer',
            transition: 'all 0.2s',
          }}>
          {saving ? 'Saving...' : `Save ${typeInfo.label.replace(/s$/, '')}`}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Main Archive Component
// ═══════════════════════════════════════════════════════════════
export default function Archive({
  stories, loadingStories, bookStoryIds,
  onViewStory, onUpdateStory, onDeleteStory,
  onAddToBook, onRemoveFromBook, onSaveNew, savingStory,
  journalEntries, onNavigateToJournal,
  // Recording props
  recordings, loadingRecordings,
  onDeleteRecording, onUnlinkRecording, onPlayRecording,
}) {
  const [showAddModal, setShowAddModal] = React.useState(false);
  const [filterType, setFilterType] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [sortBy, setSortBy] = React.useState('date-desc');
  const [editingStory, setEditingStory] = React.useState(null);
  const [editTitle, setEditTitle] = React.useState('');
  const [editContent, setEditContent] = React.useState('');
  const [savingEdit, setSavingEdit] = React.useState(false);

  // Build flat list of all items
  const allItems = React.useMemo(() => {
    const items = [];

    // Stories
    (stories || []).forEach(s => {
      items.push({ ...s, type: normalizeType(s.type) });
    });

    // Journal entries
    (journalEntries || []).forEach(j => {
      items.push({
        id: j.id,
        title: j.createdAt ? new Date(j.createdAt).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : 'Journal Entry',
        content: j.content || '',
        type: 'journal',
        createdAt: j.createdAt,
        mood: j.mood,
        audioPath: j.audioPath,
        _isJournal: true,
      });
    });

    // Recordings
    (recordings || []).forEach(r => {
      items.push({
        id: r.id,
        title: r.title || 'Untitled Recording',
        type: 'recording',
        createdAt: r.createdAt,
        shareId: r.shareId,
        storyId: r.storyId,
        s3Key: r.s3Key,
        mediaType: r.mediaType,
        sourceType: r.sourceType,
        _isRecording: true,
      });
    });

    return items;
  }, [stories, journalEntries, recordings]);

  // Filter + search + sort
  const filteredItems = React.useMemo(() => {
    let result = allItems;

    // Type filter
    if (filterType) {
      result = result.filter(item => item.type === filterType);
    }

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(item => {
        const titleMatch = (item.title || '').toLowerCase().includes(q);
        const contentMatch = (item.content || '').slice(0, 300).toLowerCase().includes(q);
        return titleMatch || contentMatch;
      });
    }

    // Sort
    result = [...result];
    if (sortBy === 'date-desc') {
      result.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    } else if (sortBy === 'date-asc') {
      result.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
    } else if (sortBy === 'title-asc') {
      result.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    }

    return result;
  }, [allItems, filterType, searchQuery, sortBy]);

  // Type counts from allItems (unfiltered)
  const typeCounts = React.useMemo(() => {
    const counts = {};
    allItems.forEach(item => { counts[item.type] = (counts[item.type] || 0) + 1; });
    return ARCHIVE_TYPES.map(t => ({ ...t, count: counts[t.id] || 0 })).filter(t => t.count > 0);
  }, [allItems]);

  const totalCount = allItems.length;

  const handleSaveNew = async (data) => {
    await onSaveNew(data);
    setShowAddModal(false);
  };

  const handleStartEdit = (story) => {
    setEditingStory(story);
    setEditTitle(story.title);
    setEditContent(story.content);
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim() || !editContent.trim() || !editingStory) return;
    setSavingEdit(true);
    try {
      await onUpdateStory(editingStory.id, editTitle.trim(), editContent.trim());
      setEditingStory(null);
      setEditTitle('');
      setEditContent('');
    } catch (err) { console.error('Edit save error:', err); }
    setSavingEdit(false);
  };

  // Loading state
  if (loadingStories) {
    return (
      <div>
        <div className="page-header">
          <h2 style={{ fontFamily: "'Lora', serif" }}>Archive</h2>
          <p style={{ fontFamily: "'Lora', serif", color: '#3c4653' }}>Loading your archive...</p>
        </div>
      </div>
    );
  }

  const sortOptions = [
    { id: 'date-desc', label: 'Newest' },
    { id: 'date-asc', label: 'Oldest' },
    { id: 'title-asc', label: 'A-Z' },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontFamily: "'Lora', serif", fontSize: '1.8rem', color: '#0f172a', marginBottom: 6 }}>Archive</h2>
        <p style={{ fontFamily: "'Lora', serif", fontSize: '1rem', color: '#3c4653', margin: 0 }}>
          Your library of stories, recordings, and everything you've written.
        </p>
      </div>

      {/* Summary bar + Add button */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 20px', background: '#fff', border: '1px solid rgba(15,23,42,0.08)',
        borderRadius: 14, marginBottom: 16, flexWrap: 'wrap', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#0f172a', fontWeight: 600 }}>
            {totalCount} {totalCount === 1 ? 'item' : 'items'}
          </span>
          {typeCounts.map(tc => (
            <span key={tc.id} style={{ fontSize: '1rem', color: '#3c4653', fontFamily: "'Lora', serif" }}>
              {tc.icon} {tc.count}
            </span>
          ))}
        </div>
        <button onClick={() => setShowAddModal(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', background: '#0f172a', color: '#fbf8f1',
            border: 'none', borderRadius: 100, cursor: 'pointer',
            fontFamily: "'Lora', serif", fontSize: '1rem', fontWeight: 600,
            transition: 'all 0.15s',
          }}
          onMouseOver={e => e.currentTarget.style.background = '#0f172a'}
          onMouseOut={e => e.currentTarget.style.background = '#0f172a'}
        >
          + Add New
        </button>
      </div>

      {/* Search bar */}
      <div style={{ marginBottom: 12 }}>
        <input
          type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search your archive..."
          style={{
            width: '100%', padding: '12px 16px', paddingLeft: 40,
            borderRadius: 12, border: '1.5px solid rgba(15,23,42,0.12)',
            fontFamily: "'Lora', serif", fontSize: '1rem', color: '#0f172a',
            background: '#fff', outline: 'none', boxSizing: 'border-box',
            transition: 'border-color 0.15s',
          }}
          onFocus={e => e.target.style.borderColor = 'rgba(15,23,42,0.3)'}
          onBlur={e => e.target.style.borderColor = 'rgba(15,23,42,0.12)'}
        />
      </div>

      {/* Filter pills + Sort */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div className="archive-filter-bar" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button onClick={() => setFilterType(null)}
            style={{
              padding: '6px 14px', borderRadius: 100, cursor: 'pointer',
              background: filterType === null ? '#0f172a' : '#fff',
              color: filterType === null ? '#fbf8f1' : '#3c4653',
              border: filterType === null ? '1.5px solid #0f172a' : '1.5px solid rgba(15,23,42,0.12)',
              fontFamily: "'Lora', serif", fontSize: '0.9rem', fontWeight: 500,
              transition: 'all 0.15s',
            }}>
            All
          </button>
          {typeCounts.map(tc => (
            <button key={tc.id} onClick={() => setFilterType(filterType === tc.id ? null : tc.id)}
              style={{
                padding: '6px 14px', borderRadius: 100, cursor: 'pointer',
                background: filterType === tc.id ? '#0f172a' : '#fff',
                color: filterType === tc.id ? '#fbf8f1' : '#3c4653',
                border: filterType === tc.id ? '1.5px solid #0f172a' : '1.5px solid rgba(15,23,42,0.12)',
                fontFamily: "'Lora', serif", fontSize: '0.9rem', fontWeight: 500,
                transition: 'all 0.15s',
              }}>
              {tc.icon} {tc.label}
            </button>
          ))}
        </div>

        {/* Sort */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: '0.85rem', color: '#3c4653', fontFamily: "'Lora', serif" }}>Sort:</span>
          {sortOptions.map(opt => (
            <button key={opt.id} onClick={() => setSortBy(opt.id)}
              style={{
                padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                background: sortBy === opt.id ? 'rgba(15,23,42,0.08)' : 'transparent',
                color: sortBy === opt.id ? '#0f172a' : '#3c4653',
                border: 'none', fontFamily: "'Lora', serif", fontSize: '0.85rem', fontWeight: 500,
                transition: 'all 0.15s',
              }}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {totalCount === 0 && (
        <div style={{
          textAlign: 'center', padding: '60px 20px',
          background: '#fff', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 16,
        }}>
          <div style={{ fontSize: '2rem', marginBottom: 16, letterSpacing: 8 }}>   📜</div>
          <p style={{ fontFamily: "'Lora', serif", fontSize: '1.1rem', color: '#0f172a', marginBottom: 8 }}>Your archive is empty</p>
          <p style={{ fontFamily: "'Lora', serif", fontSize: '0.95rem', color: '#3c4653', marginBottom: 20, maxWidth: 360, margin: '0 auto 20px' }}>
            Stories, recordings, recipes, and traditions: everything you've added appears here.
          </p>
          <button onClick={() => setShowAddModal(true)}
            style={{
              padding: '12px 28px', background: '#0f172a', color: '#fbf8f1',
              border: 'none', borderRadius: 100, cursor: 'pointer',
              fontFamily: "'Lora', serif", fontSize: '1rem', fontWeight: 600,
            }}>
            + Add Your First Item
          </button>
        </div>
      )}

      {/* No results state */}
      {totalCount > 0 && filteredItems.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '40px 20px',
          background: '#fff', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 16,
        }}>
          <p style={{ fontFamily: "'Lora', serif", fontSize: '1rem', color: '#3c4653', marginBottom: 12 }}>
            No items match your {searchQuery ? 'search' : 'filter'}.
          </p>
          <button onClick={() => { setSearchQuery(''); setFilterType(null); }}
            style={{
              padding: '8px 20px', background: '#fff', color: '#0f172a',
              border: '1.5px solid rgba(15,23,42,0.15)', borderRadius: 100, cursor: 'pointer',
              fontFamily: "'Lora', serif", fontSize: '0.95rem', fontWeight: 500,
            }}>
            Clear filters
          </button>
        </div>
      )}

      {/* Card list */}
      {filteredItems.map(item => (
        <ArchiveCard
          key={`${item.type}-${item.id}`}
          item={item}
          bookStoryIds={bookStoryIds || []}
          onView={onViewStory}
          onEdit={handleStartEdit}
          onDelete={onDeleteStory}
          onAddToBook={onAddToBook}
          onRemoveFromBook={onRemoveFromBook}
          onNavigateToJournal={item._isJournal ? onNavigateToJournal : null}
          onPlayRecording={onPlayRecording}
          onDeleteRecording={onDeleteRecording}
          onUnlinkRecording={onUnlinkRecording}
        />
      ))}

      {/* Add New Modal */}
      {showAddModal && (
        <AddNewModal
          onClose={() => setShowAddModal(false)}
          onSave={handleSaveNew}
          saving={savingStory}
        />
      )}

      {/* Edit Modal */}
      {editingStory && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }} onClick={() => setEditingStory(null)} />
          <div style={{ position: 'relative', background: '#fbf8f1', borderRadius: 20, padding: '32px 28px', maxWidth: 600, width: '100%', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontFamily: "'Lora', serif", fontSize: '1.15rem', color: '#0f172a', margin: 0 }}>Edit</h3>
              <button onClick={() => setEditingStory(null)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#3c4653', padding: 4 }}>×</button>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', marginBottom: 8 }}>Title</label>
              <input value={editTitle} onChange={e => setEditTitle(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1.5px solid rgba(15,23,42,0.15)', fontFamily: "'Lora', serif", fontSize: '1rem', color: '#0f172a', background: '#fff', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', marginBottom: 8 }}>Content</label>
              <textarea value={editContent} onChange={e => setEditContent(e.target.value)} rows={12}
                style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1.5px solid rgba(15,23,42,0.15)', fontFamily: "'Lora', serif", fontSize: '1rem', color: '#0f172a', background: '#fff', outline: 'none', resize: 'vertical', lineHeight: 1.7, boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setEditingStory(null)}
                style={{ flex: 1, padding: '14px', borderRadius: 12, border: '1.5px solid rgba(15,23,42,0.15)', background: '#fff', color: '#3c4653', fontFamily: "'Lora', serif", fontSize: '1rem', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleSaveEdit} disabled={savingEdit || !editTitle.trim() || !editContent.trim()}
                style={{
                  flex: 1, padding: '14px', borderRadius: 12, border: 'none',
                  background: (!editTitle.trim() || !editContent.trim()) ? 'rgba(15,23,42,0.1)' : '#0f172a',
                  color: (!editTitle.trim() || !editContent.trim()) ? '#3c4653' : '#fbf8f1',
                  fontFamily: "'Lora', serif", fontSize: '1rem', fontWeight: 600,
                  cursor: (!editTitle.trim() || !editContent.trim()) ? 'default' : 'pointer',
                }}>
                {savingEdit ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Export types for use in App.js
export { ARCHIVE_TYPES, TYPE_MAP, normalizeType };
